import { useParams } from "react-router-dom";

import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import Divider from "@mui/material/Divider";

import Switch from "../../../Common/Switch";
import { setPrevState } from "../../../../util/common";
import { useCallback, useEffect, useMemo, useState } from "react";
import Workspace from "../../../../service/workspace";
import ConfirmSavePopup from "./ConfirmSavePopup";
import { ButtonUI } from "../../../Common";

const NextJobForm = ({
  dataPipelineList,
  nextJobToggle,
  nextDataPipelineId,
  nextJobId,
  errorDataPipelineId,
  errorJobId,
  alternateDataPipelineId,
  alternateJobId,
  setNextJobState,
  showAlternateJob = false,
}) => {
  const { Workspace_id, Job_id } = useParams();
  const [loading, setLoading] = useState(false);
  const [openConfirm, setOpenConfirm] = useState({ open: false, name: "", idKey: "", scheduleKey: "" });
  const [pipelinesMap, setPipelinesMap] = useState(new Map());

  const nextJobList = useMemo(
    () => (nextDataPipelineId ? pipelinesMap.get(nextDataPipelineId) || [] : []),
    [nextDataPipelineId, pipelinesMap]
  );
  const errorJobList = useMemo(
    () => (errorDataPipelineId ? pipelinesMap.get(errorDataPipelineId) || [] : []),
    [errorDataPipelineId, pipelinesMap]
  );
  const alternateJobList = useMemo(
    () => (alternateDataPipelineId ? pipelinesMap.get(alternateDataPipelineId) || [] : []),
    [alternateDataPipelineId, pipelinesMap]
  );

  const handleNextJobToggle = useCallback(() => {
    setPrevState(setNextJobState, {
      nextJobToggle: !nextJobToggle,
      nextDataPipelineId: "",
      nextJobId: "",
      errorDataPipelineId: "",
      errorJobId: "",
      alternateDataPipelineId: "",
      alternateJobId: "",
    });
  }, [nextJobToggle, setNextJobState]);

  const handleSameNextJob = () => {
    if (nextDataPipelineId && nextJobId)
      setNextJobState((prev) => ({
        ...prev,
        errorDataPipelineId: prev?.nextDataPipelineId || "",
        errorJobId: prev?.nextJobId || "",
        errorSchedule: prev?.activeSchedule || "",
      }));
    else if (!(nextDataPipelineId && nextJobId) && errorDataPipelineId && errorJobId) {
      setNextJobState((prev) => ({
        ...prev,
        nextDataPipelineId: prev?.errorDataPipelineId || "",
        nextJobId: prev?.errorJobId || "",
        activeSchedule: prev?.errorSchedule || "",
      }));
    }
  };

  /**
   *
   * @param {string} name
   * @param {1 | 0} activeSchedule
   * @param {"nextJobId" | "errorJobId" | "alternateJobId"} idKey
   * @param {"activeSchedule" | "errorSchedule" | "alternateJobSchedule"} scheduleKey
   */
  const onJobChange = (name, activeSchedule, idKey, scheduleKey) => {
    if (nextJobToggle && activeSchedule === 1) {
      setPrevState(setOpenConfirm, {
        open: true,
        idKey,
        scheduleKey,
        name,
      });
    }
  };

  const handlePipelinehange = (key, value) => {
    let JobKey = "";

    if (key === "nextDataPipelineId") JobKey = "nextJobId";
    else if (key === "errorDataPipelineId") JobKey = "errorJobId";
    else if (key === "alternateDataPipelineId") JobKey = "alternateJobId";

    setPrevState(setNextJobState, {
      [key]: value,
      [JobKey]: "",
    });
  };

  const handleNextJobInputChange = (e) => {
    const nextJobInfo = nextJobList.filter((job) => job.Job_id === e.target.value)[0] || {};
    setPrevState(setNextJobState, {
      nextJobId: nextJobInfo.Job_id || "",
      activeSchedule: nextJobInfo.Enabled || "",
    });
    onJobChange(nextJobInfo.Name || "", nextJobInfo.Enabled || "", "nextJobId", "activeSchedule");
  };

  const handleErrorJobInputChange = (e) => {
    const nextJobInfo = errorJobList.filter((job) => job.Job_id === e.target.value)[0] || {};
    setPrevState(setNextJobState, {
      errorJobId: nextJobInfo.Job_id || "",
      errorSchedule: nextJobInfo.Enabled || "",
    });
    onJobChange(nextJobInfo.Name || "", nextJobInfo.Enabled || "", "errorJobId", "errorSchedule");
  };

  const handleAlternameJobInputChange = (e) => {
    const nextJobInfo = alternateJobList.filter((job) => job.Job_id === e.target.value)[0] || {};
    setPrevState(setNextJobState, {
      alternateJobId: nextJobInfo.Job_id || "",
      alternateJobSchedule: nextJobInfo.Enabled || "",
    });
    onJobChange(nextJobInfo.Name || "", nextJobInfo.Enabled || "", "alternateJobId", "alternateJobSchedule");
  };

  useEffect(() => {
    const pipelinesToFetch = [
      ...new Set(
        [nextDataPipelineId, errorDataPipelineId, alternateDataPipelineId].filter((id) => id && !pipelinesMap.has(id))
      ),
    ];

    if (!pipelinesToFetch.length) return;

    const getChainJobs = async (pipelineId) => {
      const wsService = new Workspace();
      const response = await wsService.getChainJobs(Workspace_id, Job_id, pipelineId);

      if (response.success) {
        const newMap = new Map(pipelinesMap);
        newMap.set(pipelineId, response.data.length !== 0 ? response.data : [{ Job_id: -1, Name: "Select Next Job" }]);
        setPipelinesMap(newMap);
      }

      setLoading(false);
    };

    pipelinesToFetch.forEach(getChainJobs);
  }, [nextDataPipelineId, errorDataPipelineId, alternateDataPipelineId, Job_id, Workspace_id, pipelinesMap]);

  useEffect(() => {
    const newScheduleValues = {};

    [...nextJobList, ...errorJobList, ...alternateJobList].forEach((job) => {
      if (job.Job_id === nextJobId) newScheduleValues.activeSchedule = job.Enabled || false;
      if (job.Job_id === errorJobId) newScheduleValues.errorSchedule = job.Enabled || false;
      if (job.Job_id === alternateJobId) newScheduleValues.alternateJobSchedule = job.Enabled || false;
    });

    setPrevState(setNextJobState, newScheduleValues);
  }, [alternateJobId, alternateJobList, errorJobId, errorJobList, nextJobId, nextJobList, setNextJobState]);

  const handleConfirmSaveClose = useCallback(
    (nextJobState) => {
      setPrevState(setOpenConfirm, { open: false, name: "", scheduleKey: "" });
      setNextJobState((prev) => ({ ...prev, nextJobState }));
    },
    [setNextJobState]
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={12}>
          <FormControl className="switch" component="fieldset">
            <FormGroup aria-label="position" row>
              <FormControlLabel
                disabled={loading}
                value={nextJobToggle}
                control={<Switch />}
                label="Enable Next Job"
                labelPlacement="start"
                sx={{ margin: "0", marginRight: "20px" }}
                onChange={handleNextJobToggle}
                checked={!!nextJobToggle}
              />
            </FormGroup>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={4}>
          <FormControl fullWidth disabled={loading || !nextJobToggle}>
            <InputLabel required={!!nextJobToggle}>Data Pipeline on success</InputLabel>
            <Select
              value={nextDataPipelineId}
              label="Data Pipeline on success"
              required={!!nextJobToggle}
              MenuProps={{ sx: { height: "220px" } }}
              onChange={(e) => handlePipelinehange("nextDataPipelineId", e.target.value)}
            >
              <MenuItem value="">
                <em>NO NEXT JOB ON SUCCESS</em>
              </MenuItem>
              {dataPipelineList.map((option) => (
                <MenuItem
                  key={option.DataPipeline_id}
                  value={option.DataPipeline_id}
                  disabled={option.DataPipeline_id === -1}
                >
                  {option.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={4}>
          <FormControl fullWidth disabled={loading || !nextJobToggle}>
            <InputLabel required={!!nextJobToggle}>Next job on success</InputLabel>
            <Select
              value={nextJobId}
              label="Next job on success"
              required={!!nextJobToggle}
              MenuProps={{ sx: { height: "220px" } }}
              onChange={handleNextJobInputChange}
            >
              {nextJobList.map((option) => (
                <MenuItem key={option.Job_id} value={option.Job_id} disabled={option.Job_id === -1}>
                  {option.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid size={4}>
          <FormControl fullWidth disabled={loading || !nextJobToggle}>
            <InputLabel required={!!nextJobToggle}>Data Pipeline on error</InputLabel>
            <Select
              value={errorDataPipelineId}
              label="Data Pipeline on error"
              required={!!nextJobToggle}
              MenuProps={{ sx: { height: "220px" } }}
              onChange={(e) => handlePipelinehange("errorDataPipelineId", e.target.value)}
            >
              <MenuItem value="">
                <em>NO NEXT JOB ON ERROR</em>
              </MenuItem>
              {dataPipelineList.map((option) => (
                <MenuItem
                  key={option.DataPipeline_id}
                  value={option.DataPipeline_id}
                  disabled={option.DataPipeline_id === -1}
                >
                  {option.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={4}>
          <FormControl fullWidth disabled={loading || !nextJobToggle}>
            <InputLabel required={!!nextJobToggle}>Next job on error</InputLabel>
            <Select
              value={errorJobId}
              label="Next job on error"
              required={!!nextJobToggle}
              MenuProps={{ sx: { height: "220px" } }}
              onChange={handleErrorJobInputChange}
            >
              {errorJobList.map((option) => (
                <MenuItem key={option.Job_id} value={option.Job_id} disabled={option.Job_id === -1}>
                  {option.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={4} sx={{ alignSelf: "center" }}>
          <ButtonUI
            disabled={!((nextDataPipelineId && nextJobId) || (errorDataPipelineId && errorJobId))}
            variant="contained"
            color="secondary"
            onClick={handleSameNextJob}
          >
            Same Next Job
          </ButtonUI>
        </Grid>
      </Grid>
      {showAlternateJob && (
        <>
          <Divider sx={{ pt: 2 }} />
          <Grid sx={{ mt: 1 }} size={6}>
            <Typography variant="subtitle2">
              Route to an alternate job if the query returns a vaule of 0 or false.
            </Typography>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={4}>
              <FormControl fullWidth disabled={loading || !nextJobToggle}>
                <InputLabel required={!!nextJobToggle}>Data Pipeline if False</InputLabel>
                <Select
                  value={alternateDataPipelineId}
                  label="Data Pipeline if False"
                  required={!!nextJobToggle}
                  MenuProps={{ sx: { height: "220px" } }}
                  onChange={(e) => handlePipelinehange("alternateDataPipelineId", e.target.value)}
                >
                  <MenuItem value="">
                    <em>NO NEXT JOB IF FALSE</em>
                  </MenuItem>
                  {dataPipelineList.map((option) => (
                    <MenuItem
                      key={option.DataPipeline_id}
                      value={option.DataPipeline_id}
                      disabled={option.DataPipeline_id === -1}
                    >
                      {option.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4}>
              <FormControl
                fullWidth
                disabled={loading || !nextJobToggle}
                error={nextJobId && nextJobId === alternateJobId}
              >
                <InputLabel required={!!nextJobToggle}>Next job if False</InputLabel>
                <Select
                  value={alternateJobId}
                  label="Next job if False"
                  required={!!nextJobToggle}
                  MenuProps={{ sx: { height: "220px" } }}
                  onChange={handleAlternameJobInputChange}
                >
                  {alternateJobList.map((option) => (
                    <MenuItem key={option.Job_id} value={option.Job_id} disabled={option.Job_id === -1}>
                      {option.Name}
                    </MenuItem>
                  ))}
                </Select>
                {nextJobId && nextJobId === alternateJobId && (
                  <FormHelperText>Success and Alternative job outcomes cannot be the same.</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </>
      )}
      {openConfirm?.open && (
        <ConfirmSavePopup
          loading={loading}
          idKey={openConfirm.idKey}
          scheduleKey={openConfirm.scheduleKey}
          job={openConfirm.name}
          handleConfirmSaveClose={handleConfirmSaveClose}
        />
      )}
    </>
  );
};

export default NextJobForm;
